import { Component, computed, inject, signal } from '@angular/core';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BehaviorSubject, combineLatest, firstValueFrom, of, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Validators } from '@angular/forms';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  ResidentDialogComponent,
  ResidentDialogResult,
} from './dialogs/resident-dialog.component';
import { CheckoutResidentDialogComponent } from './dialogs/checkout-resident-dialog.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  BuildingEquipmentService,
  BuildingSectionDetailDto,
  BuildingSectionService,
  FlatDetailDto,
  FlatMergeDto,
  FlatService,
  ResidentItemDto,
  ResidentService,
  SensorItemDto,
} from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { CurrentUserService } from '@services/current-user.service';
import { queryParam, routeParam } from '@/utils/signals/router';

@Component({
  standalone: false,
  selector: 'wr-flat-detail-page',
  templateUrl: './flat-detail-page.component.html',
  styleUrl: './flat-detail-page.component.scss'
})
export class FlatDetailPageComponent implements PendingChanges {
  readonly router = inject(Router);
  readonly flatService = inject(FlatService);
  readonly residentService = inject(ResidentService);
  readonly toastrService = inject(NbToastrService);
  readonly dialogService = inject(NbDialogService);
  readonly buildingSectionService = inject(BuildingSectionService);
  readonly buildingEquipmentService = inject(BuildingEquipmentService);
  readonly currentUserService = inject(CurrentUserService);

  isEditing = false;
  firstSubmit = false;
  form?: ReturnType<typeof this.createFormFromDetail>;
  saving$ = new BehaviorSubject(false);
  flatId = routeParam('id', true);
  buildingId = queryParam('buildingId', false);

  selectedBuildingSectionId = signal<string | null>(null);
  // BUG-10 fix: track the section subscription so it can be unsubscribed on form recreation
  private sectionSubscription?: Subscription;

  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.flatId() === 'new');

  readonly heatDimension = 'ENERGY';
  readonly hotWaterDimension = 'VOLUME';
  readonly unitMeterPurpose = SensorItemDto.PurposeEnum.UnitMeter;
  readonly unitMeterPurposes = [SensorItemDto.PurposeEnum.UnitMeter];

  readonly buildingSections = signal<BuildingSectionDetailDto[]>([]);

  readonly buildingEquipments = asyncComputed(() => {
    const selectedBuildingSectionId = this.selectedBuildingSectionId();
    if (selectedBuildingSectionId) {
      return this.buildingEquipmentService.findAllByBuildingSectionId(selectedBuildingSectionId);
    }
    return [];
  });

  readonly residents = asyncComputed<ResidentItemDto[]>(() => {
    const flatId = this.flatId();
    if (flatId && flatId !== 'new') {
      return this.residentService.findAll({ flatId });
    }
    return [];
  });


  constructor() {
    const id = this.flatId();
    const buildingId = this.buildingId();

    if (!id) {
      // BUG-9 fix: navigate to buildings (not the non-existent 'units' route)
      this.router.navigate(['buildings']);
      return;
    }

    if (id === 'new') {
      // BUG-8 fix: guard missing buildingId before creating new flat form
      if (!buildingId) {
        this.router.navigate(['buildings']);
        return;
      }
      this.canEdit.set(true);
      this.isEditing = true;
      // Load sections first so nb-select has options when the form value is set
      firstValueFrom(this.buildingSectionService.findAllByBuildingId(buildingId)).then(sections => {
        this.buildingSections.set(sections);
        this.form = this.createFormFromDetail(null);
      });
    } else {
      // Load flat and sections together so nb-select has options when writeValue is called
      const sections$ = buildingId
        ? this.buildingSectionService.findAllByBuildingId(buildingId)
        : of<BuildingSectionDetailDto[]>([]);

      combineLatest([this.flatService.findById(id), sections$]).subscribe(([flat, sections]) => {
        this.buildingSections.set(sections);
        this.canEdit.set(flat.permissions.canEdit);
        this.form = this.createFormFromDetail(flat);
        this.form?.disable();
      });
    }
  }

  createFormFromDetail(dto: FlatDetailDto | null) {
    // BUG-10 fix: unsubscribe previous section subscription before recreating form
    this.sectionSubscription?.unsubscribe();

    const form = createForm(({ field }) => {
      return {
        id: field({ value: dto?.id }),
        flatNumber: field({ value: dto?.flatNumber, validators: [Validators.required, Validators.min(1)] }),
        floor: field({ value: dto?.floor, validators: [Validators.required, Validators.pattern('^-?[0-9]+$')] }),
        doorNumber: field({ value: dto?.doorNumber, validators: [Validators.required] }),
        buildingSectionId: field({ value: dto?.buildingSection?.id }),
        size: field({ value: dto?.size, validators: [Validators.required] }),
        buildingEquipmentId: field({ value: dto?.buildingEquipment?.id }),
        color: field({ value: dto?.color ?? '#000000' }),
        heatSensor: field({ value: dto?.heatSensor }),
        hotWaterSensor: field({ value: dto?.hotWaterSensor }),
      };
    });

    if (dto?.buildingSection) {
      this.selectedBuildingSectionId.set(dto.buildingSection.id);
    }

    const buildingSectionControl = form.controls.buildingSectionId;
    this.sectionSubscription = buildingSectionControl.valueChanges.subscribe((buildingSectionId) => {
      const selectedBuildingSectionId = this.selectedBuildingSectionId();
      if (selectedBuildingSectionId !== buildingSectionId) {
        form.controls.buildingEquipmentId.setValue(undefined);
      }
      this.selectedBuildingSectionId.set(buildingSectionId || null);
    });

    return form;
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  async onDelete(): Promise<void> {
    if (await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Wohnung löschen',
        message: 'Bist du sicher, dass du die Wohnung löschen willst?',
      },
    }).onClose) && this.form?.value.id) {
      await firstValueFrom(this.flatService.deleteById(this.form?.value.id));
      // BUG-3 fix: navigate back to the building detail page after deletion
      await this.router.navigate(['building', this.buildingId()]);
      this.toastrService.success('Wohnung wurde erfolgreich gelöscht', 'Wohnung');
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.toastrService.danger('Wohnung kann nicht gespeichert werden, es fehlen noch Daten.', 'Wohnung');
      return;
    }
    try {
      this.saving$.next(true);
      const buildingId = this.buildingId();
      const formValue = this.form?.value;
      const flatDto: FlatMergeDto = {
        id: formValue?.id,
        flatNumber: formValue?.flatNumber,
        size: formValue?.size,
        floor: formValue?.floor,
        doorNumber: formValue?.doorNumber,
        color: formValue?.color,
        buildingId: buildingId!,
        buildingSectionId: formValue?.buildingSectionId,
        buildingEquipmentId: formValue?.buildingEquipmentId,
        heatSensorId: formValue?.heatSensor?.id,
        hotWaterSensorId: formValue?.hotWaterSensor?.id,
      };
      const flat = await firstValueFrom(this.flatService.merge(flatDto));
      this.isEditing = false;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['flat', flat.id], { queryParams: { buildingId: this.buildingId() } }).then(() => {
          this.toastrService.success('Wohnung wurde erfolgreich gespeichert', 'Wohnung');
        });
      });
    } catch (e: unknown) {
      console.error(e);
      this.toastrService.danger('Wohnung konnte nicht gespeichert werden', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  async cancel(): Promise<void> {
    window.history.back();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  async navigateBack(): Promise<void> {
    window.history.back();
  }

  async addResident(): Promise<void> {
    const flatId = this.flatId();
    if (!flatId) return;

    const result: ResidentDialogResult | null = await firstValueFrom(
      this.dialogService.open(ResidentDialogComponent, {
        context: { otherResidents: this.residents() ?? [] },
      }).onClose,
    );
    if (!result) return;

    try {
      await firstValueFrom(this.residentService.merge({
        customerNumber: result.customerNumber,
        startDate: result.startDate,
        endDate: result.endDate ?? undefined,
        clearEndDate: !result.endDate,
        flatId,
      }));
      this.residents.reload();
      this.toastrService.success('Bewohner wurde hinzugefügt', 'Bewohner');
    } catch {
      this.toastrService.danger('Bewohner konnte nicht gespeichert werden', 'Fehler');
    }
  }

  async editResident(resident: ResidentItemDto): Promise<void> {
    const others = (this.residents() ?? []).filter(r => r.id !== resident.id);
    const result: ResidentDialogResult | null = await firstValueFrom(
      this.dialogService.open(ResidentDialogComponent, {
        context: { existingResident: resident, otherResidents: others },
      }).onClose,
    );
    if (!result) return;

    try {
      await firstValueFrom(this.residentService.merge({
        id: resident.id,
        customerNumber: result.customerNumber,
        startDate: result.startDate,
        endDate: result.endDate ?? undefined,
        clearEndDate: !result.endDate,
      }));
      this.residents.reload();
      this.toastrService.success('Bewohner wurde aktualisiert', 'Bewohner');
    } catch {
      this.toastrService.danger('Bewohner konnte nicht gespeichert werden', 'Fehler');
    }
  }

  async checkoutResident(resident: ResidentItemDto): Promise<void> {
    const endDate: string | null = await firstValueFrom(
      this.dialogService.open(CheckoutResidentDialogComponent, {
        context: {
          customerNumber: resident.customerNumber,
          startDate: resident.startDate,
        },
      }).onClose,
    );
    if (!endDate) return;

    try {
      await firstValueFrom(this.residentService.merge({
        id: resident.id,
        endDate,
      }));
      this.residents.reload();
      this.toastrService.success('Auszug wurde erfasst', 'Bewohner');
    } catch {
      this.toastrService.danger('Auszug konnte nicht gespeichert werden', 'Fehler');
    }
  }

  async removeResident(resident: ResidentItemDto): Promise<void> {
    const confirmed = await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Bewohner entfernen',
        message: `Möchten Sie den Bewohner "${resident.customerNumber}" wirklich entfernen?`,
      },
    }).onClose);
    if (!confirmed) return;

    try {
      await firstValueFrom(this.residentService.deleteById(resident.id));
      this.residents.reload();
      this.toastrService.success('Bewohner wurde entfernt', 'Bewohner');
    } catch {
      this.toastrService.danger('Bewohner konnte nicht entfernt werden', 'Fehler');
    }
  }
}
